import MarkdownWidgetComponent from "@AppBuilderShared/components/shapediver/ui/MarkdownWidgetComponent";
import AppBuilderPage from "@AppBuilderShared/pages/appbuilder/AppBuilderPage";
import AlertPage from "@AppBuilderShared/pages/misc/AlertPage";
import LoaderPage from "@AppBuilderShared/pages/misc/LoaderPage";
import { IAppBuilderSettingsSession } from "@AppBuilderShared/types/shapediver/appbuilder";
import {getEnvironmentIdentifier} from "@AppBuilderLib/shared/lib/platform/environment";
import React from "react";
import { useCeruleoHostedSessionBootstrap } from "../../ceruleo/useCeruleoHostedSessionBootstrap";
import { useApplyCeruleoTheme } from "../../ceruleo/useApplyCeruleoTheme";
import { useApplyOverlayCameraOffset } from "../../ceruleo/useApplyOverlayCameraOffset";
import CeruleoLandingPage from "../../components/ui/CeruleoLandingPage";
import CorsErrorMessage from "../../components/ui/CorsErrorMessage";
import {getIframeErrorGuidance} from "../../patches/iframe-embedding-config";

interface Props extends Partial<IAppBuilderSettingsSession> {
	example?: string;
}

/**
 * Wrapper around the shared AppBuilderPage that adds CORS error handling
 */
export default function AppBuilderPageCorsHandler(props: Props) {
	// Create a wrapper that catches errors and shows CORS help
	const [error, setError] = React.useState<Error | null>(null);
	const ceruleoBootstrap = useCeruleoHostedSessionBootstrap();
	useApplyCeruleoTheme(ceruleoBootstrap.appvTheme);
	useApplyOverlayCameraOffset(ceruleoBootstrap.appvTheme);
	const env = getEnvironmentIdentifier();

	// For external domains, show helpful message immediately if no parameters
	const params = new URLSearchParams(window.location.search);
	const hasParams = params.size > 0;

	if (env === "unknown" && !hasParams) {
		return (
			<AlertPage>
				<CeruleoLandingPage />
			</AlertPage>
		);
	}

	React.useEffect(() => {
		// Listen for unhandled promise rejections (CORS errors)
		const handleRejection = (event: PromiseRejectionEvent) => {
			const reason = event.reason;
			if (reason && typeof reason === "object" && reason.message) {
				const message = reason.message.toString();
				if (
					message.includes("CORS") ||
					message.includes("iframe embedding") ||
					message.includes("Access-Control-Allow-Origin") ||
					message.includes("preflight request")
				) {
					console.error("CORS error detected:", reason);
					setError(reason);
					event.preventDefault(); // Prevent default error handling
				}
			}
		};

		window.addEventListener("unhandledrejection", handleRejection);
		return () =>
			window.removeEventListener("unhandledrejection", handleRejection);
	}, []);

	if (error) {
		// Check if this looks like an iframe embedding error
		const isIframeError =
			error.message.includes("iframe") ||
			error.message.includes("XMLHttpRequest") ||
			error.message.includes("shapediver.com/api");

		return (
			<AlertPage title="Connection Error">
				{isIframeError ? (
					<MarkdownWidgetComponent anchorTarget="_blank">
						{getIframeErrorGuidance(error)}
					</MarkdownWidgetComponent>
				) : (
					<CorsErrorMessage
						error={error}
						hostname={window.location.hostname}
					/>
				)}
			</AlertPage>
		);
	}

	if (ceruleoBootstrap.bootstrapping && ceruleoBootstrap.loading) {
		return <LoaderPage />;
	}

	if (ceruleoBootstrap.bootstrapping && ceruleoBootstrap.error) {
		return (
			<AlertPage title="Hosted session error">
				{ceruleoBootstrap.error.message}
			</AlertPage>
		);
	}

	return (
		<AppBuilderPage
			key={[
				ceruleoBootstrap.tokenId ?? "no-token",
				ceruleoBootstrap.selectedScanPayload?.scanSessionId ?? "no-scan",
				ceruleoBootstrap.selectedScanPayload?.asset.accessUrl ?? "no-url",
			].join(":")}
			{...props}
			{...(ceruleoBootstrap.session ?? {})}
		/>
	);
}
